import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "elitestock_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

const client = axios.create({ baseURL: API, timeout: 30000 });

client.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Request failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  }
  if (detail && typeof detail === "object") {
    return detail.error || detail.msg || JSON.stringify(detail);
  }
  return String(detail);
}

// ---- photos ----
export async function searchPhotos({ query, page = 1, per_page = 24, orientation, color }) {
  const params = { query, page, per_page };
  if (orientation) params.orientation = orientation;
  if (color) params.color = color;
  const { data } = await client.get("/photos/search", { params });
  return data;
}
export async function curatedPhotos({ page = 1, per_page = 24 } = {}) {
  const { data } = await client.get("/photos/curated", { params: { page, per_page } });
  return data;
}
export async function photoDetail(id) {
  const { data } = await client.get(`/photos/${id}`);
  return data;
}
export async function batchThumbs(queries) {
  const { data } = await client.post("/photos/thumbs", { queries });
  return data.thumbs || {};
}
export async function recordSearchEvent(query) {
  try { await client.post("/search-events", { query }); } catch { /* non-blocking */ }
}
export async function getTrending(limit = 8) {
  const { data } = await client.get("/search-events/trending", { params: { limit } });
  return data.trending || [];
}

// ---- auth ----
export async function apiSignup({ name, email, password }) {
  const { data } = await client.post("/auth/signup", { name, email, password });
  return data; // { user, token }
}
export async function apiLogin({ email, password }) {
  const { data } = await client.post("/auth/login", { email, password });
  return data; // { user, token }
}
export async function apiMe() {
  const { data } = await client.get("/auth/me");
  return data;
}
export async function apiLogout() {
  try { await client.post("/auth/logout"); } catch { /* stateless, ignore errors */ }
}

// ---- favorites ----
export async function getFavoriteIds() {
  const { data } = await client.get("/favorites/ids");
  return data.ids || [];
}
export async function getFavorites() {
  const { data } = await client.get("/favorites");
  return data.favorites || [];
}
export async function addFavorite(photo) {
  const { data } = await client.post("/favorites", photo);
  return data;
}
export async function removeFavorite(photoId) {
  const { data } = await client.delete(`/favorites/${photoId}`);
  return data;
}

// ---- collections ----
export async function getCollections() {
  const { data } = await client.get("/collections");
  return data.collections || [];
}
export async function createCollection(name) {
  const { data } = await client.post("/collections", { name });
  return data;
}
export async function renameCollection(id, name) {
  const { data } = await client.patch(`/collections/${id}`, { name });
  return data;
}
export async function deleteCollection(id) {
  const { data } = await client.delete(`/collections/${id}`);
  return data;
}
export async function getCollectionPhotos(id) {
  const { data } = await client.get(`/collections/${id}/photos`);
  return data; // { collection, photos }
}
export async function addPhotoToCollection(id, photo) {
  const { data } = await client.post(`/collections/${id}/photos`, photo);
  return data;
}
export async function removePhotoFromCollection(id, photoId) {
  const { data } = await client.delete(`/collections/${id}/photos/${photoId}`);
  return data;
}
export async function getCollectionsForPhoto(photoId) {
  const { data } = await client.get(`/collections-for-photo/${photoId}`);
  return data.collection_ids || [];
}

// ---- downloads ----
export async function getDownloads() {
  const { data } = await client.get("/downloads");
  return data.downloads || [];
}

/** Trigger a download by fetching the file with auth header, then saving it locally. */
export async function downloadPhoto(photoId, size = "large") {
  const resp = await client.get(`/downloads/${photoId}`, {
    params: { size },
    responseType: "blob",
  });
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `elitestock-${photoId}-${size}.jpg`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return { ok: true };
}
